export const WILDLIFE_SYSTEM_VERSION = 2;

const WILDLIFE_ENTITY_TYPE = 'wildlife';
const TWO_PI = Math.PI * 2;
const BEHAVIORS = Object.freeze(['idle', 'forage', 'flee', 'pursue', 'corpse']);
const LIFECYCLES = Object.freeze(['alive', 'corpse']);

export const WILDLIFE_SPECIES = deepFreeze({
  'marsh-insect': {
    id: 'marsh-insect',
    label: 'Marsh Insect',
    role: 'prey',
    habitat: 'marsh',
    forageSpeed: 0.25,
    alertSpeed: 0.75,
    detectionRadius: 1.5,
    decisionSeconds: 0.75,
    maxHealth: 1,
    nutrition: 4,
    growth: { length: 0.01, mass: 0.002, points: 0.25 },
    traversal: {
      radius: 0.04,
      height: 0.08,
      capabilities: { swim: false, climb: true, crawl: true },
    },
  },
  bullfrog: {
    id: 'bullfrog',
    label: 'Bullfrog',
    role: 'prey',
    habitat: 'wetland',
    forageSpeed: 0.45,
    alertSpeed: 1.5,
    detectionRadius: 3,
    decisionSeconds: 1.25,
    maxHealth: 18,
    nutrition: 22,
    growth: { length: 0.04, mass: 0.015, points: 1 },
    traversal: {
      radius: 0.16,
      height: 0.22,
      capabilities: { swim: true, climb: false, crawl: true },
    },
  },
  'mud-minnow': {
    id: 'mud-minnow',
    label: 'Mud Minnow',
    role: 'prey',
    habitat: 'aquatic',
    forageSpeed: 0.35,
    alertSpeed: 1.25,
    detectionRadius: 2,
    decisionSeconds: 0.9,
    maxHealth: 8,
    nutrition: 12,
    growth: { length: 0.025, mass: 0.008, points: 0.5 },
    traversal: {
      radius: 0.07,
      height: 0.1,
      capabilities: { swim: true, climb: false, crawl: false },
    },
  },
  'great-blue-heron': {
    id: 'great-blue-heron',
    label: 'Great Blue Heron',
    role: 'predator',
    habitat: 'shoreline',
    forageSpeed: 0.8,
    alertSpeed: 1.8,
    detectionRadius: 6,
    decisionSeconds: 1.5,
    maxHealth: 60,
    nutrition: 50,
    growth: { length: 0.12, mass: 0.05, points: 4 },
    traversal: {
      radius: 0.3,
      height: 1.2,
      capabilities: { swim: true, climb: false, crawl: false },
    },
  },
});

const clone = (value) => value === undefined
  ? undefined
  : JSON.parse(JSON.stringify(value));

function deepFreeze(value) {
  if (!value || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const child of Object.values(value)) deepFreeze(child);
  return Object.freeze(value);
}

function canonicalize(value) {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, canonicalize(value[key])]),
  );
}

function checksumPayload(payload) {
  const serialized = JSON.stringify(canonicalize(payload));
  let hash = 0x811c9dc5;
  for (let index = 0; index < serialized.length; index += 1) {
    hash ^= serialized.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv1a32:${(hash >>> 0).toString(16).padStart(8, '0')}`;
}

export function computeWildlifeChecksum(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    throw new TypeError('snapshot must be an object');
  }
  const payload = clone(snapshot);
  delete payload.checksum;
  return checksumPayload(payload);
}

function assertPlainObject(value, name) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${name} must be an object`);
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    throw new TypeError(`${name} must be a plain object`);
  }
  return value;
}

function assertMethod(value, method, name) {
  if (!value || typeof value[method] !== 'function') {
    throw new TypeError(`${name} must provide ${method}()`);
  }
}

function assertNonEmptyString(value, name) {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${name} must be a non-empty string`);
  }
  return value.trim();
}

function assertFinite(value, name) {
  if (!Number.isFinite(value)) throw new RangeError(`${name} must be finite`);
  return value;
}

function assertFiniteRange(value, min, max, name) {
  if (!Number.isFinite(value) || value < min || value > max) {
    throw new RangeError(`${name} must be between ${min} and ${max}`);
  }
  return value;
}

function assertFiniteNonNegative(value, name) {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative finite number`);
  }
  return value;
}

function assertPositiveFinite(value, name) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
  return value;
}

function assertNonNegativeInteger(value, name) {
  if (!Number.isInteger(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative integer`);
  }
  return value;
}

function assertPositiveInteger(value, name) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }
  return value;
}

function normalizeAngle(value) {
  const angle = assertFinite(value, 'heading') % TWO_PI;
  return angle < 0 ? angle + TWO_PI : angle;
}

function normalizePosition(position = {}, fallback = { x: 0, y: 0, z: 0 }) {
  assertPlainObject(position, 'position');
  return {
    x: assertFinite(position.x ?? fallback.x, 'position.x'),
    y: assertFinite(position.y ?? fallback.y, 'position.y'),
    z: assertFinite(position.z ?? fallback.z, 'position.z'),
  };
}

function normalizeBehavior(value) {
  const behavior = assertNonEmptyString(value, 'behavior');
  if (!BEHAVIORS.includes(behavior)) {
    throw new RangeError(`Unsupported wildlife behavior: ${behavior}`);
  }
  return behavior;
}

function normalizeLifecycle(value) {
  const lifecycle = assertNonEmptyString(value, 'lifecycle');
  if (!LIFECYCLES.includes(lifecycle)) {
    throw new RangeError(`Unsupported wildlife lifecycle: ${lifecycle}`);
  }
  return lifecycle;
}

function getSpecies(speciesId) {
  const normalizedId = assertNonEmptyString(speciesId, 'speciesId');
  const species = WILDLIFE_SPECIES[normalizedId];
  if (!species) throw new RangeError(`Unknown wildlife species: ${normalizedId}`);
  return species;
}

function hashValue(...parts) {
  const text = parts.join('|');
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0x100000000;
}

function distanceSquared(left, right) {
  return ((left.x - right.x) ** 2)
    + ((left.y - right.y) ** 2)
    + ((left.z - right.z) ** 2);
}

function headingToward(from, to) {
  return normalizeAngle(Math.atan2(to.x - from.x, to.z - from.z));
}

function entityTags(record) {
  return [record.lifecycle, record.habitat, record.role, 'wildlife'].sort();
}

function entityComponents(record) {
  return {
    speciesId: record.speciesId,
    role: record.role,
    habitat: record.habitat,
    lifecycle: record.lifecycle,
    generation: record.generation,
    health: record.health,
    maxHealth: record.maxHealth,
    behavior: record.behavior,
    heading: record.heading,
    speed: record.speed,
    targetEntityId: record.targetEntityId,
    corpseCreatedAt: record.corpseCreatedAt,
    lastDamageSourceId: record.lastDamageSourceId,
    wildlifeRevision: record.revision,
  };
}

function traversalProfile(record) {
  const profile = getSpecies(record.speciesId).traversal;
  return {
    radius: profile.radius,
    height: profile.height,
    capabilities: profile.capabilities,
  };
}

function buildRecord({
  entityId,
  speciesId,
  generation,
  revision,
  lifecycle,
  health,
  position,
  heading,
  speed,
  behavior,
  targetEntityId,
  corpseCreatedAt,
  lastDamageSourceId,
  nextDecisionTime,
  tick,
  simulationTime,
  stepSeconds,
}) {
  const species = getSpecies(speciesId);
  const normalizedLifecycle = normalizeLifecycle(lifecycle);
  const normalizedHealth = assertFiniteRange(health, 0, species.maxHealth, 'health');
  const normalizedBehavior = normalizeBehavior(behavior);
  const normalizedSpeed = assertFiniteNonNegative(speed, 'speed');
  const normalizedTarget = targetEntityId === null
    ? null
    : assertNonEmptyString(targetEntityId, 'targetEntityId');
  const normalizedCorpseCreatedAt = corpseCreatedAt === null
    ? null
    : assertFiniteNonNegative(corpseCreatedAt, 'corpseCreatedAt');
  const normalizedDamageSource = lastDamageSourceId === null
    ? null
    : assertNonEmptyString(lastDamageSourceId, 'lastDamageSourceId');

  if (normalizedLifecycle === 'alive') {
    if (normalizedHealth <= 0) throw new RangeError('alive wildlife must have positive health');
    if (normalizedBehavior === 'corpse') throw new RangeError('alive wildlife cannot use corpse behavior');
    if (normalizedCorpseCreatedAt !== null) throw new RangeError('alive wildlife cannot have corpseCreatedAt');
  } else {
    if (normalizedHealth !== 0) throw new RangeError('corpse wildlife must have zero health');
    if (normalizedBehavior !== 'corpse') throw new RangeError('corpse wildlife must use corpse behavior');
    if (normalizedSpeed !== 0) throw new RangeError('corpse wildlife must have zero speed');
    if (normalizedTarget !== null) throw new RangeError('corpse wildlife cannot have a target');
    if (normalizedCorpseCreatedAt === null) throw new RangeError('corpse wildlife requires corpseCreatedAt');
  }

  return deepFreeze({
    version: WILDLIFE_SYSTEM_VERSION,
    entityId: assertNonEmptyString(entityId, 'entityId'),
    speciesId: species.id,
    role: species.role,
    habitat: species.habitat,
    generation: assertPositiveInteger(generation, 'generation'),
    revision: assertPositiveInteger(revision, 'revision'),
    lifecycle: normalizedLifecycle,
    health: normalizedHealth,
    maxHealth: species.maxHealth,
    position: normalizePosition(position),
    heading: normalizeAngle(heading),
    speed: normalizedLifecycle === 'corpse' ? 0 : normalizedSpeed,
    behavior: normalizedBehavior,
    targetEntityId: normalizedTarget,
    corpseCreatedAt: normalizedCorpseCreatedAt,
    lastDamageSourceId: normalizedDamageSource,
    nextDecisionTime: assertFiniteNonNegative(nextDecisionTime, 'nextDecisionTime'),
    tick: tick === null ? null : assertNonNegativeInteger(tick, 'tick'),
    simulationTime: assertFiniteNonNegative(simulationTime, 'simulationTime'),
    stepSeconds: stepSeconds === null
      ? null
      : assertPositiveFinite(stepSeconds, 'stepSeconds'),
  });
}

function normalizeSpawn(speciesId, options = {}) {
  assertPlainObject(options, 'options');
  if (options.revision !== undefined
    || options.lifecycle !== undefined
    || options.health !== undefined
    || options.behavior !== undefined
    || options.checksum !== undefined) {
    throw new RangeError('wildlife revision, lifecycle, health, behavior, and checksum are authoritative');
  }
  const species = getSpecies(speciesId);
  return {
    species,
    generation: assertPositiveInteger(options.generation ?? 1, 'generation'),
    entityId: options.entityId === undefined
      ? undefined
      : assertNonEmptyString(options.entityId, 'entityId'),
    position: normalizePosition(options.position ?? {}),
    heading: normalizeAngle(options.heading ?? 0),
  };
}

function normalizeRecordSnapshot(entry) {
  assertPlainObject(entry, 'wildlife entry');
  const record = buildRecord({
    entityId: entry.entityId,
    speciesId: entry.speciesId,
    generation: entry.generation,
    revision: entry.revision,
    lifecycle: entry.lifecycle,
    health: entry.health,
    position: entry.position,
    heading: entry.heading,
    speed: entry.speed,
    behavior: entry.behavior,
    targetEntityId: entry.targetEntityId,
    corpseCreatedAt: entry.corpseCreatedAt,
    lastDamageSourceId: entry.lastDamageSourceId,
    nextDecisionTime: entry.nextDecisionTime,
    tick: entry.tick,
    simulationTime: entry.simulationTime,
    stepSeconds: entry.stepSeconds,
  });
  if (entry.maxHealth !== record.maxHealth) {
    throw new RangeError('wildlife maxHealth must match its species definition');
  }
  return record;
}

export class WildlifeSystem {
  constructor({ entityWorld, traversalWorld = null }) {
    assertMethod(entityWorld, 'spawn', 'entityWorld');
    assertMethod(entityWorld, 'update', 'entityWorld');
    assertMethod(entityWorld, 'remove', 'entityWorld');
    assertMethod(entityWorld, 'get', 'entityWorld');
    assertMethod(entityWorld, 'snapshot', 'entityWorld');
    assertMethod(entityWorld, 'restore', 'entityWorld');
    if (traversalWorld !== null) {
      assertMethod(traversalWorld, 'resolveMovement', 'traversalWorld');
      assertMethod(traversalWorld, 'commitActorResolution', 'traversalWorld');
      assertMethod(traversalWorld, 'syncActor', 'traversalWorld');
      assertMethod(traversalWorld, 'removeActor', 'traversalWorld');
      assertMethod(traversalWorld, 'snapshot', 'traversalWorld');
      assertMethod(traversalWorld, 'restore', 'traversalWorld');
    }
    this.entityWorld = entityWorld;
    this.traversalWorld = traversalWorld;
    this.records = new Map();
    this.revision = 0;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
  }

  #syncTraversal(record, resolution = undefined) {
    if (!this.traversalWorld) return null;
    if (resolution) {
      return this.traversalWorld.commitActorResolution(resolution, {
        tick: record.tick,
        simulationTime: record.simulationTime,
        stepSeconds: record.stepSeconds,
      });
    }
    return this.traversalWorld.syncActor({
      actorId: record.entityId,
      actorKind: `wildlife:${record.speciesId}`,
      position: record.position,
      ...traversalProfile(record),
      tick: record.tick,
      simulationTime: record.simulationTime,
      stepSeconds: record.stepSeconds,
    });
  }

  listSpecies() {
    return Object.values(WILDLIFE_SPECIES).map((species) => species);
  }

  getSpeciesDefinition(speciesId) {
    return getSpecies(speciesId);
  }

  ownsEntity(id) {
    return this.records.has(assertNonEmptyString(id, 'id'));
  }

  spawn(speciesId, options = {}) {
    const normalized = normalizeSpawn(speciesId, options);
    const initial = {
      entityId: normalized.entityId ?? 'pending',
      speciesId: normalized.species.id,
      generation: normalized.generation,
      revision: 1,
      lifecycle: 'alive',
      health: normalized.species.maxHealth,
      position: normalized.position,
      heading: normalized.heading,
      speed: 0,
      behavior: 'idle',
      targetEntityId: null,
      corpseCreatedAt: null,
      lastDamageSourceId: null,
      nextDecisionTime: 0,
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      const entity = this.entityWorld.spawn({
        ...(normalized.entityId ? { id: normalized.entityId } : {}),
        type: WILDLIFE_ENTITY_TYPE,
        position: normalized.position,
        tags: ['alive', normalized.species.habitat, normalized.species.role, 'wildlife'].sort(),
        components: entityComponents(buildRecord({ ...initial, entityId: normalized.entityId ?? 'pending' })),
      });
      const record = buildRecord({ ...initial, entityId: entity.id });
      if (entity.id !== normalized.entityId) {
        this.entityWorld.update(entity.id, {
          position: record.position,
          tags: entityTags(record),
          components: entityComponents(record),
        });
      }
      this.#syncTraversal(record);
      this.records.set(entity.id, record);
      this.revision += 1;
      return record;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  get(id) {
    return this.records.get(assertNonEmptyString(id, 'id')) ?? null;
  }

  list({
    speciesId = undefined,
    role = undefined,
    behavior = undefined,
    lifecycle = undefined,
  } = {}) {
    const normalizedSpecies = speciesId === undefined ? null : getSpecies(speciesId).id;
    const normalizedRole = role === undefined ? null : assertNonEmptyString(role, 'role');
    if (normalizedRole !== null && normalizedRole !== 'prey' && normalizedRole !== 'predator') {
      throw new RangeError('role must be prey or predator');
    }
    const normalizedBehavior = behavior === undefined ? null : normalizeBehavior(behavior);
    const normalizedLifecycle = lifecycle === undefined ? null : normalizeLifecycle(lifecycle);
    return [...this.records.values()]
      .filter((record) => normalizedSpecies === null || record.speciesId === normalizedSpecies)
      .filter((record) => normalizedRole === null || record.role === normalizedRole)
      .filter((record) => normalizedBehavior === null || record.behavior === normalizedBehavior)
      .filter((record) => normalizedLifecycle === null || record.lifecycle === normalizedLifecycle)
      .sort((left, right) => left.entityId.localeCompare(right.entityId));
  }

  remove(id) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const record = this.records.get(normalizedId);
    if (!record) return null;
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      if (this.entityWorld.get(normalizedId)) this.entityWorld.remove(normalizedId);
      if (this.traversalWorld) this.traversalWorld.removeActor(normalizedId);
      this.records.delete(normalizedId);
      this.revision += 1;
      return record;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  damage(id, amount, {
    tick = this.currentStep.tick,
    simulationTime = this.currentStep.simulationTime,
    sourceEntityId = null,
  } = {}) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const normalizedAmount = assertPositiveFinite(amount, 'amount');
    const normalizedTick = tick === null ? null : assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedSource = sourceEntityId === null
      ? null
      : assertNonEmptyString(sourceEntityId, 'sourceEntityId');
    const current = this.records.get(normalizedId);
    if (!current) throw new RangeError(`Unknown wildlife entity: ${normalizedId}`);
    if (current.lifecycle !== 'alive') throw new RangeError('Only living wildlife can take damage');

    const health = Math.max(0, current.health - normalizedAmount);
    const lifecycle = health === 0 ? 'corpse' : 'alive';
    const next = buildRecord({
      ...current,
      revision: current.revision + 1,
      lifecycle,
      health,
      speed: lifecycle === 'corpse' ? 0 : current.speed,
      behavior: lifecycle === 'corpse' ? 'corpse' : current.behavior,
      targetEntityId: lifecycle === 'corpse' ? null : current.targetEntityId,
      corpseCreatedAt: lifecycle === 'corpse' ? normalizedSimulationTime : null,
      lastDamageSourceId: normalizedSource,
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
    });
    const entity = this.entityWorld.get(normalizedId);
    if (!entity) throw new Error(`Wildlife entity is missing: ${normalizedId}`);
    if (entity.type !== WILDLIFE_ENTITY_TYPE) {
      throw new RangeError(`Wildlife entity has invalid type: ${entity.type}`);
    }
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      this.entityWorld.update(normalizedId, {
        position: next.position,
        tags: entityTags(next),
        components: entityComponents(next),
      });
      this.#syncTraversal(next);
      this.records.set(normalizedId, next);
      this.revision += 1;
      return next;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  consume(id) {
    const normalizedId = assertNonEmptyString(id, 'id');
    const record = this.records.get(normalizedId);
    if (!record) throw new RangeError(`Unknown wildlife entity: ${normalizedId}`);
    if (record.lifecycle !== 'corpse') throw new RangeError('Only wildlife corpses can be consumed');
    const entity = this.entityWorld.get(normalizedId);
    if (!entity) throw new Error(`Wildlife entity is missing: ${normalizedId}`);
    if (entity.type !== WILDLIFE_ENTITY_TYPE) {
      throw new RangeError(`Wildlife entity has invalid type: ${entity.type}`);
    }
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      this.entityWorld.remove(normalizedId);
      if (this.traversalWorld) this.traversalWorld.removeActor(normalizedId);
      this.records.delete(normalizedId);
      this.revision += 1;
      return record;
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }
  }

  step({ stepSeconds, tick, simulationTime, snakeState = null, tickSeed }) {
    const normalizedStepSeconds = assertPositiveFinite(stepSeconds, 'stepSeconds');
    const normalizedTick = assertNonNegativeInteger(tick, 'tick');
    const normalizedSimulationTime = assertFiniteNonNegative(simulationTime, 'simulationTime');
    const normalizedTickSeed = assertNonNegativeInteger(tickSeed, 'tickSeed');
    if (normalizedTickSeed > 0xffffffff) throw new RangeError('tickSeed must be an unsigned 32-bit integer');

    const snake = snakeState && snakeState.lifecycle === 'alive' ? snakeState : null;
    const nextRecords = new Map();
    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    let changed = false;

    try {
      for (const current of this.list()) {
        if (current.lifecycle === 'corpse') {
          nextRecords.set(current.entityId, current);
          continue;
        }
        const species = getSpecies(current.speciesId);
        let behavior = current.behavior;
        let heading = current.heading;
        let targetEntityId = null;
        let nextDecisionTime = current.nextDecisionTime;

        const detectsSnake = snake
          && distanceSquared(current.position, snake.position) <= species.detectionRadius ** 2;

        if (detectsSnake) {
          targetEntityId = snake.entityId;
          if (species.role === 'prey') {
            behavior = 'flee';
            heading = normalizeAngle(headingToward(current.position, snake.position) + Math.PI);
          } else {
            behavior = 'pursue';
            heading = headingToward(current.position, snake.position);
          }
          nextDecisionTime = normalizedSimulationTime + species.decisionSeconds;
        } else if (behavior === 'flee'
          || behavior === 'pursue'
          || normalizedSimulationTime >= nextDecisionTime) {
          const choice = hashValue(normalizedTickSeed, current.entityId, current.revision, 'behavior');
          behavior = choice < 0.35 ? 'idle' : 'forage';
          heading = normalizeAngle(
            hashValue(normalizedTickSeed, current.entityId, current.revision, 'heading') * TWO_PI,
          );
          const jitter = 0.75
            + (hashValue(normalizedTickSeed, current.entityId, current.revision, 'decision') * 0.5);
          nextDecisionTime = normalizedSimulationTime + (species.decisionSeconds * jitter);
        }

        const requestedSpeed = behavior === 'idle'
          ? 0
          : behavior === 'forage'
            ? species.forageSpeed
            : species.alertSpeed;
        const requestedPosition = {
          x: current.position.x + (Math.sin(heading) * requestedSpeed * normalizedStepSeconds),
          y: current.position.y,
          z: current.position.z + (Math.cos(heading) * requestedSpeed * normalizedStepSeconds),
        };
        const traversalResolution = this.traversalWorld
          ? this.traversalWorld.resolveMovement({
            actorId: current.entityId,
            actorKind: `wildlife:${current.speciesId}`,
            from: current.position,
            to: requestedPosition,
            ...traversalProfile(current),
          })
          : null;
        const position = traversalResolution?.position ?? requestedPosition;
        const resolvedDistance = Math.hypot(
          position.x - current.position.x,
          position.z - current.position.z,
        );
        const speed = requestedSpeed === 0 ? 0 : resolvedDistance / normalizedStepSeconds;
        const next = buildRecord({
          ...current,
          revision: current.revision + 1,
          position,
          heading,
          speed,
          behavior,
          targetEntityId,
          nextDecisionTime,
          tick: normalizedTick,
          simulationTime: normalizedSimulationTime,
          stepSeconds: normalizedStepSeconds,
        });
        const entity = this.entityWorld.get(current.entityId);
        if (!entity) throw new Error(`Wildlife entity is missing: ${current.entityId}`);
        if (entity.type !== WILDLIFE_ENTITY_TYPE) {
          throw new RangeError(`Wildlife entity has invalid type: ${entity.type}`);
        }
        this.entityWorld.update(current.entityId, {
          position: next.position,
          tags: entityTags(next),
          components: entityComponents(next),
        });
        this.#syncTraversal(next, traversalResolution ?? undefined);
        nextRecords.set(next.entityId, next);
        changed = true;
      }
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }

    this.records = nextRecords;
    if (changed) this.revision += 1;
    this.currentStep = {
      tick: normalizedTick,
      simulationTime: normalizedSimulationTime,
      stepSeconds: normalizedStepSeconds,
    };
    return this.getStateRef();
  }

  snapshot() {
    const payload = {
      version: WILDLIFE_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      count: this.records.size,
      wildlife: this.list().map((record) => clone(record)),
    };
    return deepFreeze({
      ...payload,
      checksum: computeWildlifeChecksum(payload),
    });
  }

  exportSnapshot() {
    return JSON.stringify(this.snapshot());
  }

  restore(snapshot) {
    const parsed = typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot;
    assertPlainObject(parsed, 'snapshot');
    if (parsed.version !== WILDLIFE_SYSTEM_VERSION) {
      throw new RangeError(`Unsupported wildlife snapshot version: ${parsed.version}`);
    }
    const revision = assertNonNegativeInteger(parsed.revision, 'snapshot.revision');
    const tick = parsed.tick === null ? null : assertNonNegativeInteger(parsed.tick, 'snapshot.tick');
    const simulationTime = assertFiniteNonNegative(parsed.simulationTime, 'snapshot.simulationTime');
    const stepSeconds = parsed.stepSeconds === null
      ? null
      : assertPositiveFinite(parsed.stepSeconds, 'snapshot.stepSeconds');
    if (!Array.isArray(parsed.wildlife)) throw new TypeError('snapshot.wildlife must be an array');
    const count = assertNonNegativeInteger(parsed.count, 'snapshot.count');
    if (count !== parsed.wildlife.length) {
      throw new RangeError('snapshot.count must match snapshot.wildlife.length');
    }
    const records = parsed.wildlife.map(normalizeRecordSnapshot)
      .sort((left, right) => left.entityId.localeCompare(right.entityId));
    const ids = new Set(records.map((record) => record.entityId));
    if (ids.size !== records.length) throw new RangeError('snapshot contains duplicate wildlife IDs');
    if (records.some((record) => record.revision > revision)) {
      throw new RangeError('wildlife revision cannot exceed snapshot.revision');
    }

    const payload = {
      version: WILDLIFE_SYSTEM_VERSION,
      revision,
      tick,
      simulationTime,
      stepSeconds,
      count,
      wildlife: records.map((record) => clone(record)),
    };
    if (typeof parsed.checksum !== 'string' || parsed.checksum !== computeWildlifeChecksum(payload)) {
      throw new RangeError('snapshot checksum is invalid');
    }

    for (const record of records) {
      const existing = this.entityWorld.get(record.entityId);
      if (existing && existing.type !== WILDLIFE_ENTITY_TYPE) {
        throw new RangeError(`Snapshot entity has invalid type: ${record.entityId}`);
      }
    }

    const worldBackup = this.entityWorld.snapshot();
    const traversalBackup = this.traversalWorld?.snapshot() ?? null;
    try {
      for (const current of this.records.values()) {
        if (!ids.has(current.entityId)) {
          if (this.entityWorld.get(current.entityId)) this.entityWorld.remove(current.entityId);
          if (this.traversalWorld) this.traversalWorld.removeActor(current.entityId);
        }
      }
      for (const record of records) {
        const existing = this.entityWorld.get(record.entityId);
        if (existing) {
          this.entityWorld.update(record.entityId, {
            position: record.position,
            tags: entityTags(record),
            components: entityComponents(record),
          });
        } else {
          this.entityWorld.spawn({
            id: record.entityId,
            type: WILDLIFE_ENTITY_TYPE,
            position: record.position,
            tags: entityTags(record),
            components: entityComponents(record),
          });
        }
        this.#syncTraversal(record);
      }
    } catch (error) {
      this.entityWorld.restore(worldBackup);
      if (traversalBackup) this.traversalWorld.restore(traversalBackup);
      throw error;
    }

    this.records = new Map(records.map((record) => [record.entityId, record]));
    this.revision = revision;
    this.currentStep = { tick, simulationTime, stepSeconds };
    return this.snapshot();
  }

  reset({ removeEntities = true } = {}) {
    if (typeof removeEntities !== 'boolean') throw new TypeError('removeEntities must be a boolean');
    if (removeEntities) {
      for (const record of this.records.values()) {
        if (this.entityWorld.get(record.entityId)) this.entityWorld.remove(record.entityId);
      }
    }
    if (this.traversalWorld) {
      for (const record of this.records.values()) this.traversalWorld.removeActor(record.entityId);
    }
    this.records = new Map();
    this.revision = 0;
    this.currentStep = {
      tick: null,
      simulationTime: 0,
      stepSeconds: null,
    };
    return this.getStatus();
  }

  getStateRef() {
    const snapshot = this.snapshot();
    return deepFreeze({
      version: snapshot.version,
      revision: snapshot.revision,
      tick: snapshot.tick,
      count: snapshot.count,
      aliveCount: this.list({ lifecycle: 'alive' }).length,
      corpseCount: this.list({ lifecycle: 'corpse' }).length,
      checksum: snapshot.checksum,
    });
  }

  getStatus() {
    return deepFreeze({
      version: WILDLIFE_SYSTEM_VERSION,
      revision: this.revision,
      tick: this.currentStep.tick,
      simulationTime: this.currentStep.simulationTime,
      stepSeconds: this.currentStep.stepSeconds,
      count: this.records.size,
      aliveCount: this.list({ lifecycle: 'alive' }).length,
      corpseCount: this.list({ lifecycle: 'corpse' }).length,
      preyCount: this.list({ role: 'prey' }).length,
      predatorCount: this.list({ role: 'predator' }).length,
    });
  }
}
